// Typed fetch client for the Worker API. Owned by the spec; pages import `api` and the shared types.
import type { ApiError } from '../../src/shared/api';

export class ApiFailure extends Error {
  constructor(public status: number, public body: ApiError) {
    super(body.message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'vd', // CSRF guard: every mutating API call must carry it (checked server-side)
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: 'bad_json', message: text.slice(0, 200) }; }
  if (!res.ok) {
    const err = (data && typeof data === 'object' && 'error' in (data as object))
      ? (data as ApiError)
      : { error: 'http_' + res.status, message: `Request failed (${res.status}).` };
    if (res.status === 401 && !path.startsWith('/api/auth/')) window.dispatchEvent(new CustomEvent('vd:unauthenticated'));
    throw new ApiFailure(res.status, err);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
  /** multipart/binary upload (voicemail recordings). */
  upload: async <T>(path: string, blob: Blob, fields: Record<string, string>): Promise<T> => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    fd.append('file', blob, 'recording' + (blob.type === 'audio/mpeg' ? '.mp3' : '.wav'));
    const res = await fetch(path, { method: 'POST', body: fd, credentials: 'same-origin', headers: { 'X-Requested-With': 'vd' } });
    const data = await res.json().catch(() => ({ error: 'bad_json', message: 'Upload failed.' }));
    if (!res.ok) throw new ApiFailure(res.status, data as ApiError);
    return data as T;
  },
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiFailure) return e.body.message;
  if (e instanceof Error) return e.message;
  return String(e);
}
